import { hydrateFavourites } from "./favourites";

/**
 * Search-map behaviour for /properties/?view=map, and ONLY there.
 *
 * initMap() in googleMaps.js is shared with the single-property map and the
 * owner's property form. It calls into this file only when the map's data-map
 * settings say `"search": true`, which archive-property.php alone sets - so
 * every other map keeps exactly the behaviour it had.
 *
 * What it adds:
 *   - below xl, a bottom sheet for a tapped house instead of an InfoWindow;
 *   - at xl and up, the same compact card in a single shared InfoWindow;
 *   - tapping the map, the close button or pressing Escape dismisses either;
 *   - the map's height measured from where it really starts (--lh-map-top);
 *   - desktop list <-> pin linking: hovering a card lights its pin, tapping a
 *     pin scrolls its card into view in the list and flashes it.
 *
 * SURVIVING A RE-RENDER is the constraint everything here is built around.
 * Every filter change makes FiltersFull.vue replace .google-map's innerHTML
 * and the list's, then call googleMaps() again, which builds a brand new map.
 * So: per-map state lives in `current` and is REPLACED on every init; the
 * listeners that outlive a map (list hover, Escape, the resize observer) are
 * bound once, on stable ancestors, and always read `current`; and the bottom
 * sheet lives outside .google-map so the re-render cannot destroy it.
 */

/** The live search map. Replaced on every init. */
let current = null;

let documentListenersBound = false;
let resizeObserver = null;

/** Which house the pointer is over in the list, so mouseover can bail early. */
let hoveredId = null;

const FLASH_MS = 1600;

const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Called by initMap() before markers are added. Returns the state object
 * initMarker() registers markers against.
 */
export function createSearchMap(element, map) {
    const wrapper = element.closest('[data-ajax-results-map]');
    const sheet = wrapper ? wrapper.querySelector('[data-map-sheet]') : null;

    // A previous map's sheet content belongs to houses that may no longer be
    // in the results. Clear it before anything else can show it.
    if (sheet) {
        closeSheet(sheet);
    }

    const state = {
        map,
        element,
        wrapper,
        sheet,
        markers: new Map(),
        selectedId: null,
        hoverId: null,
        // One InfoWindow for the whole map rather than one per marker: only
        // one card is ever open, and swapping content means there is nothing
        // to keep in step. headerDisabled removes Google's header and close
        // button, since the card brings its own (the CSS also hides Google's
        // in case this API version ignores the option).
        infoWindow: new google.maps.InfoWindow({ headerDisabled: true }),
        // Until somebody moves the map, a change in its height may refit it.
        // Once they have, their view is theirs.
        touched: false,
        lastMarkerTap: 0,
        // Fits the map to its houses. initMap() calls it once the markers are
        // in; the resize observer calls it again until someone moves the map.
        refit: () => fitToMarkers(map, element),
    };

    // Zoom buttons and the round camera control to the top right. Google
    // puts both bottom right by default, which on a phone is exactly where
    // the house card sits: the card covered the zoom buttons, and then half
    // of the camera control once those had moved (both seen on dev, 7 Oct
    // 2026). Pinch still zooms, but a control nobody can reach is a control
    // nobody can see is there.
    map.setOptions({
        zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_TOP },
        cameraControlOptions: { position: google.maps.ControlPosition.RIGHT_TOP },
    });

    // Tapping empty map closes the card. The time check is insurance: if a
    // marker tap ever also reached the map's own click handler, it would
    // close the card the same tap had just opened. 50ms because a propagated
    // event arrives within the same tap; anything longer starts swallowing a
    // person's genuine second tap on the map.
    map.addListener('click', () => {
        if (Date.now() - state.lastMarkerTap < 50) {
            return;
        }

        close(state);
    });

    map.addListener('dragstart', () => { state.touched = true; });

    // Google's own close path (and any future one) must clear the selection.
    state.infoWindow.addListener('closeclick', () => setSelected(state, null));

    current = state;

    bindDocumentListenersOnce();

    return state;
}

/**
 * Kept clear round the houses when fitting, in pixels. More at the top
 * because a pin is drawn ABOVE its position (anchored at its foot), so a
 * house on the northern edge needs its own height in hand.
 */
const FIT_PADDING = { top: 48, right: 24, bottom: 24, left: 24 };

/** Never zoom in further than this on fit, however close the houses are. */
const FIT_MAX_ZOOM = 11;

/**
 * Fit the map to its houses at the zoom that actually fits - INCLUDING
 * fractional zoom.
 *
 * Why not fitBounds(): on dev (7 Oct 2026) it opened at zoom 5 at both 1440
 * and 390 wide, showing the UK with Norway and northern France round it. The
 * houses run 50.3-57.7N, and with the Highland ones in, zoom 6 genuinely does
 * not fit, and fitBounds only tries whole levels. Worked out here instead it
 * lands around 5.7 at both widths, with every house still on screen.
 *
 * That needs isFractionalZoomEnabled, which initMap() switches on for the
 * search map only. Google defaults it to OFF unless the map renders as
 * vector, and without it setZoom(5.7) is rounded to 6 and floored back to 5
 * below - so the first version of this still opened at exactly 5 (and at
 * exactly 6 on a filtered list), which is what gave it away. An earlier note
 * here said this was a vector map because the wheel read 5.24; that was a
 * reading taken mid-animation, not proof.
 *
 * Web Mercator, which is what Google draws: at zoom z the world is
 * 256 * 2^z pixels square, x is linear in longitude and y in
 * ln(tan(pi/4 + lat/2)). So the widest zoom that fits a span is a log2 of
 * the space available over the span's size at zoom 0.
 *
 * If fractional zoom is ever not honoured, Google rounds setZoom() to the
 * nearest whole level - which can round UP and clip the edge houses. So once
 * the map settles the result is checked, and floored if it came out higher
 * than asked. That check waits for 'idle', which a background tab may never
 * fire, so it is a safety net and not the mechanism: a hidden-tab reading on
 * dev showed zoom 6 with 18 houses outside the view, the rounding left
 * standing because the net never ran.
 */
export function fitToMarkers(map, element) {
    // A position may come back as a plain {lat, lng} or as a LatLng whose
    // lat and lng are methods - read either.
    const read = (value) => Number(typeof value === 'function' ? value() : value);

    const positions = (map.markers || [])
        .map((marker) => marker.position)
        .filter(Boolean)
        .map((p) => ({ lat: read(p.lat), lng: read(p.lng) }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));

    if (!positions.length) {
        return;
    }

    const lats = positions.map((p) => p.lat);
    const lngs = positions.map((p) => p.lng);
    const mercY = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));

    const north = Math.max(...lats);
    const south = Math.min(...lats);
    const east = Math.max(...lngs);
    const west = Math.min(...lngs);

    const yTop = mercY(north);
    const yBottom = mercY(south);
    const yMid = (yTop + yBottom) / 2;
    const xMid = (east + west) / 2;
    const toLat = (y) => ((2 * Math.atan(Math.exp(y)) - Math.PI / 2) * 180) / Math.PI;

    const width = element.clientWidth - FIT_PADDING.left - FIT_PADDING.right;
    const height = element.clientHeight - FIT_PADDING.top - FIT_PADDING.bottom;

    if (positions.length === 1 || width <= 0 || height <= 0) {
        map.setCenter({ lat: toLat(yMid), lng: xMid });

        return;
    }

    // Span in pixels at zoom 0, then how many doublings fit the space.
    const spanX = ((east - west) / 360) * 256;
    const spanY = ((yTop - yBottom) / (2 * Math.PI)) * 256;

    const zoomX = spanX > 0 ? Math.log2(width / spanX) : FIT_MAX_ZOOM;
    const zoomY = spanY > 0 ? Math.log2(height / spanY) : FIT_MAX_ZOOM;

    // Two decimal places: enough precision, and a stable number to compare
    // against what Google hands back.
    const zoom = Math.floor(Math.min(zoomX, zoomY, FIT_MAX_ZOOM) * 100) / 100;

    // The padding is uneven (more at the top), so the houses belong in the
    // middle of the PADDED box, not the middle of the map. Move the map's
    // centre by half the difference, converted from pixels at this zoom:
    // the houses sit lower when the top margin is bigger, so the centre goes
    // north; likewise west for a bigger left margin. Without this the
    // northernmost houses landed inside the top margin, measured.
    const pixelsPerUnitAtZoom = 256 * Math.pow(2, zoom);
    const shiftDown = (FIT_PADDING.top - FIT_PADDING.bottom) / 2;
    const shiftRight = (FIT_PADDING.left - FIT_PADDING.right) / 2;

    map.setCenter({
        lat: toLat(yMid + (shiftDown * 2 * Math.PI) / pixelsPerUnitAtZoom),
        lng: xMid - (shiftRight * 360) / pixelsPerUnitAtZoom,
    });
    map.setZoom(zoom);

    google.maps.event.addListenerOnce(map, 'idle', () => {
        if (typeof map.getZoom === 'function' && map.getZoom() > zoom + 0.001) {
            map.setZoom(Math.floor(zoom));
        }
    });
}

/**
 * Called by initMarker() for each marker on a search map.
 *
 * @param {object} state From createSearchMap().
 * @param {number} id    The property ID from the marker's data.
 * @param {object} marker The AdvancedMarkerElement.
 * @param {string} html  The compact card markup from propertiesMapHtml().
 */
export function registerSearchMarker(state, id, marker, html) {
    if (!id) {
        return;
    }

    state.markers.set(id, { marker, html });

    google.maps.event.addListener(marker, 'click', () => {
        state.touched = true;
        state.lastMarkerTap = Date.now();
        open(state, id);
    });
}

/**
 * Size the map to the screen it starts on (below xl; the CSS ignores the
 * variable at xl and up).
 *
 * Measured from the TOP OF THE DOCUMENT, not the viewport, so a browser that
 * restores a scroll position does not shrink the map to a sliver.
 *
 * Re-measured whenever anything above the map changes height: the filter bar
 * is a Vue app that can mount after this runs, and the results heading changes
 * length with the count. If the height changes before anyone has touched the
 * map, it is refitted to its houses - otherwise the first fit would have been
 * worked out for a box of the wrong size. Once they have moved it, their view
 * is left alone.
 */
export function sizeSearchMap(wrapper) {
    if (!wrapper) {
        return;
    }

    measure(wrapper);

    if (resizeObserver || !('ResizeObserver' in window)) {
        return;
    }

    const section = wrapper.closest('section');

    if (!section) {
        return;
    }

    let refitTimer = null;

    // Bound once. It looks the wrapper up again each time rather than holding
    // the one it was created with, and refits whichever map is CURRENT.
    resizeObserver = new ResizeObserver(() => {
        const live = document.querySelector('[data-ajax-results-map="properties"]');

        if (!live || !measure(live) || !current || current.touched || !current.refit) {
            return;
        }

        window.clearTimeout(refitTimer);

        // Give Google a moment to notice its container has changed size, or
        // the fit is worked out for the old box.
        refitTimer = window.setTimeout(() => {
            if (current && !current.touched && current.refit) {
                current.refit();
            }
        }, 150);
    });

    Array.from(section.children).forEach((child) => resizeObserver.observe(child));
}

/**
 * Write --lh-map-top. Returns true when the value actually changed, so the
 * observer only refits for a real change.
 */
function measure(wrapper) {
    const top = Math.round(wrapper.getBoundingClientRect().top + window.scrollY);

    if (top <= 0) {
        return false;
    }

    // Written on the results grid rather than the map's own wrapper, so the
    // desktop rule that sizes the GRID can read it too; the wrapper inherits
    // it. At xl the map and the list share one row, so the wrapper's top is
    // the grid's top.
    const target = wrapper.closest('[data-ajax-results="properties"]') || wrapper;
    const value = `${top}px`;

    if (target.style.getPropertyValue('--lh-map-top') === value) {
        return false;
    }

    target.style.setProperty('--lh-map-top', value);

    return true;
}

/**
 * Whether this screen uses the bottom sheet. Read from the CSS (the sheet is
 * display:none at xl and up) so the breakpoint lives in one place.
 */
function usesSheet(state) {
    return !!state.sheet && window.getComputedStyle(state.sheet).display !== 'none';
}

function open(state, id) {
    const entry = state.markers.get(id);

    if (!entry) {
        return;
    }

    setSelected(state, id);

    const popup = buildPopup(entry.html, state.sheet, () => close(state));

    if (usesSheet(state)) {
        state.infoWindow.close();
        state.sheet.replaceChildren(popup);
        state.sheet.classList.add('is-open');
        keepPinClearOfSheet(state, entry.marker);
    } else {
        if (state.sheet) {
            closeSheet(state.sheet);
        }

        state.infoWindow.setContent(popup);
        state.infoWindow.open(state.map, entry.marker);
        revealInList(id);
    }

    // Same delay the InfoWindow path has always used: the heart has to be in
    // the document before hydrateFavourites() can find and bind it.
    setTimeout(() => hydrateFavourites(), 10);
}

/**
 * Below xl the card covers the bottom of the map, so a house tapped in the
 * lower part of it ended up behind its own card (seen on dev, 7 Oct 2026).
 * Move the map up just far enough to bring the pin above the card - and no
 * further than keeps it below the map's top edge, so a pin can never be
 * pushed out of view the other way.
 *
 * Measured from the pin as drawn rather than worked out from its lat/lng: the
 * pin's own box already allows for it being drawn above its position, and it
 * needs no projection.
 */
// 20px rather than a hairline: the pin is measured as it is drawn at the
// moment of the tap, and the lit pin then grows by 30% about its centre,
// which takes its foot a few pixels further down towards the card.
const SHEET_CLEARANCE = 20;

function keepPinClearOfSheet(state, marker) {
    const pin = marker && (marker.element || marker.content);

    if (!pin || typeof state.map.panBy !== 'function') {
        return;
    }

    const pinBox = pin.getBoundingClientRect();
    const sheetBox = state.sheet.getBoundingClientRect();
    const mapBox = state.element.getBoundingClientRect();

    // A clustered house has no pin on the map to move.
    if (!pinBox.width || !sheetBox.height) {
        return;
    }

    const needed = pinBox.bottom - (sheetBox.top - SHEET_CLEARANCE);
    const room = pinBox.top - (mapBox.top + SHEET_CLEARANCE);
    const shift = Math.min(needed, room);

    if (shift > 0) {
        state.map.panBy(0, Math.round(shift));
    }
}

function close(state) {
    state.infoWindow.close();

    if (state.sheet) {
        closeSheet(state.sheet);
    }

    setSelected(state, null);
}

function closeSheet(sheet) {
    sheet.classList.remove('is-open');
    sheet.replaceChildren();
}

/**
 * The card plus a close button, in one wrapper the sheet and the InfoWindow
 * both use. The label comes from the template (data-close-label) so it is
 * translatable.
 */
function buildPopup(html, sheet, onClose) {
    const popup = document.createElement('div');

    popup.className = 'google-map-popup';
    popup.innerHTML = html;

    const button = document.createElement('button');

    button.type = 'button';
    button.className = 'google-map-popup__close';
    button.setAttribute('aria-label', (sheet && sheet.dataset.closeLabel) || 'Close');
    button.innerHTML = '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>';

    button.addEventListener('click', (event) => {
        // Not a click on the map underneath: Google would otherwise see it.
        event.stopPropagation();
        onClose();
    });

    popup.prepend(button);

    return popup;
}

/** The tapped house. Persists until closed or another pin is tapped. */
function setSelected(state, id) {
    state.selectedId = id;
    paintMarkers(state);
}

/** The house under the pointer in the list. Desktop only in practice. */
function setHovered(state, id) {
    state.hoverId = id;
    paintMarkers(state);
}

/**
 * One function decides every pin's state, so hover and selection cannot fight
 * over the class. A clustered marker has no map (MarkerClusterer takes it
 * off), so it is skipped rather than lit inside a cluster nobody can see.
 */
function paintMarkers(state) {
    state.markers.forEach((entry, id) => {
        const lit = id === state.selectedId || id === state.hoverId;
        const content = entry.marker.content;

        if (!content || !content.classList) {
            return;
        }

        if (lit && entry.marker.map) {
            content.classList.add('google-map__marker--active');
            entry.marker.zIndex = 1000;
        } else {
            content.classList.remove('google-map__marker--active');
            entry.marker.zIndex = null;
        }
    });
}

/**
 * Bring the house's card into view in the LIST'S OWN scroll container.
 *
 * Not scrollIntoView(): that scrolls every scrollable ancestor, the page
 * included, so tapping a pin would yank the whole layout. Setting the
 * container's scrollTop moves the list and nothing else.
 */
function revealInList(id) {
    const list = document.querySelector('[data-ajax-results="properties"] [data-properties]');

    if (!list) {
        return;
    }

    const card = list.querySelector(`[data-property-id="${id}"]`);

    if (!card) {
        return;
    }

    const listBox = list.getBoundingClientRect();
    const cardBox = card.getBoundingClientRect();
    const fullyVisible = cardBox.top >= listBox.top && cardBox.bottom <= listBox.bottom;

    if (!fullyVisible) {
        list.scrollTo({
            top: list.scrollTop + (cardBox.top - listBox.top) - 16,
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
    }

    card.classList.add('is-map-flash');

    window.setTimeout(() => card.classList.remove('is-map-flash'), FLASH_MS);
}

/**
 * Listeners that must outlive any single map. Bound once per page and always
 * act on `current`, so they keep working across every re-render.
 */
function bindDocumentListenersOnce() {
    if (documentListenersBound) {
        return;
    }

    documentListenersBound = true;

    // Delegated: the list's cards are replaced on every filter change, so
    // per-card listeners would be lost the first time anyone filtered.
    document.addEventListener('mouseover', (event) => {
        const card = event.target.closest
            ? event.target.closest('[data-properties] [data-property-id]')
            : null;

        const id = card ? parseInt(card.dataset.propertyId, 10) : null;

        if (id === hoveredId) {
            return;
        }

        hoveredId = id;

        if (current) {
            setHovered(current, id);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && current && current.selectedId !== null) {
            close(current);
        }
    });
}
